import React, { useCallback, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Image from 'react-bootstrap/Image';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Spinner from 'react-bootstrap/Spinner';
import { PersonFill, ShieldFill, Trash3 } from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { useAuth } from '../contexts/AuthContext';
import { formatLocalDateTimeFromUTC, getUserTimeZoneLabel } from '../utils/datetime';
import { passwordPolicyError } from '../utils/passwordPolicy';
import './Profile-new.css';

interface UserProfileResponse {
  id: number;
  email: string;
  username: string;
  full_name?: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

interface UserProfileStatsResponse {
  project_count?: number;
  documents_uploaded?: number;
  test_cases_generated?: number;
}

interface TrustedMfaDeviceResponse {
  id: number;
  device_name?: string;
  created_at?: string;
  last_used_at?: string | null;
  expires_at?: string;
}

const Profile: React.FC = () => {
  const AUTH_NOTICE_MESSAGE = 'authNoticeMessage';
  const PASSWORD_CHANGED_NOTICE = 'Password changed successfully. Please sign in again.';
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [stats, setStats] = useState<{ projects: number; documents: number; testCases: number }>({ projects: 0, documents: 0, testCases: 0 });
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(true);
  const [isSavingProfile, setIsSavingProfile] = useState<boolean>(false);
  const [isSavingPassword, setIsSavingPassword] = useState<boolean>(false);

  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [variant, setVariant] = useState<'success' | 'danger'>('success');
  const [activeKey, setActiveKey] = useState<string>('profile');
  const [forcePasswordSet, setForcePasswordSet] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [trustedDevices, setTrustedDevices] = useState<TrustedMfaDeviceResponse[]>([]);
  const [isLoadingTrustedDevices, setIsLoadingTrustedDevices] = useState<boolean>(false);
  const [revokingDeviceId, setRevokingDeviceId] = useState<number | 'all' | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const fetchProfile = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    try {
      setIsLoadingProfile(true);
      const res = await apiClient.get(API_ENDPOINTS.USER_DETAIL(user.id));
      const p = res.data as UserProfileResponse;
      setProfile(p);
      setAvatarUrl(`https://ui-avatars.com/api/?name=${encodeURIComponent(p.full_name || p.username)}&background=1b2d45&color=fff&size=128`);
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Failed to load profile');
      setVariant('danger');
    } finally {
      setIsLoadingProfile(false);
    }
  }, [user?.id]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiClient.get(`${API_ENDPOINTS.PROFILE}/stats`);
      const s = res.data as UserProfileStatsResponse;
      setStats({
        projects: s.project_count ?? 0,
        documents: s.documents_uploaded ?? 0,
        testCases: s.test_cases_generated ?? 0,
      });
    } catch {}
  }, []);

  const fetchTrustedDevices = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    try {
      setIsLoadingTrustedDevices(true);
      const res = await apiClient.get(API_ENDPOINTS.MFA_TRUSTED_DEVICES);
      setTrustedDevices(Array.isArray(res.data?.devices) ? res.data.devices : []);
    } catch {
      setTrustedDevices([]);
    } finally {
      setIsLoadingTrustedDevices(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    fetchProfile();
    fetchStats();
    fetchTrustedDevices();
  }, [fetchProfile, fetchStats, fetchTrustedDevices, user?.id]);

  useEffect(() => {
    const setPwd = searchParams.get('set_password');
    if (setPwd === '1') {
      setForcePasswordSet(true);
      setActiveKey('security');
    }
  }, [searchParams]);

  const handleSave = async () => {
    try {
      if (!profile) return;
      setIsSavingProfile(true);
      const res = await apiClient.put(API_ENDPOINTS.UPDATE_PROFILE, {
        email: profile.email,
        username: profile.username,
        full_name: profile.full_name,
      });
      setProfile(res.data as UserProfileResponse);
      setMessage('Profile updated successfully!');
      setVariant('success');
      setIsEditing(false);
      setTimeout(() => setMessage(null), 3000);
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Failed to update profile');
      setVariant('danger');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCancel = () => {
    fetchProfile();
    setIsEditing(false);
    setMessage(null);
  };

  const handleRevokeTrustedDevice = async (deviceId: number): Promise<void> => {
    try {
      setRevokingDeviceId(deviceId);
      await apiClient.delete(`${API_ENDPOINTS.MFA_TRUSTED_DEVICES}/${deviceId}`);
      setMessage('Trusted browser removed.');
      setVariant('success');
      await fetchTrustedDevices();
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Failed to remove trusted browser');
      setVariant('danger');
    } finally {
      setRevokingDeviceId(null);
    }
  };

  const handleRevokeAllTrustedDevices = async (): Promise<void> => {
    try {
      setRevokingDeviceId('all');
      await apiClient.delete(API_ENDPOINTS.MFA_TRUSTED_DEVICES);
      setMessage('All trusted browsers removed.');
      setVariant('success');
      await fetchTrustedDevices();
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Failed to remove trusted browsers');
      setVariant('danger');
    } finally {
      setRevokingDeviceId(null);
    }
  };

  const createdAtLabel = profile?.created_at
    ? formatLocalDateTimeFromUTC(profile.created_at)
    : 'Not available';
  const lastLoginLabel = profile?.last_login
    ? formatLocalDateTimeFromUTC(profile.last_login)
    : 'Never';
  const displayName = profile?.full_name || profile?.username || 'Account User';
  const accountStateLabel = profile?.is_active ? 'Active account' : 'Inactive account';

  return (
    <div className="profile-new-page">
      <div className="profile-new-hero">
        <div>
          <div className="profile-new-eyebrow">Account Center</div>
          <h1>My Account</h1>
          <p>Manage your profile and security settings.</p>
        </div>
        <div className="profile-new-hero-meta">
          <span className="profile-new-status-pill">{accountStateLabel}</span>
          <span className="profile-new-timezone">Times shown in {getUserTimeZoneLabel()}</span>
        </div>
      </div>

      {message && <Alert variant={variant} className="profile-new-alert">{message}</Alert>}

      {isLoadingProfile ? (
        <Card className="profile-new-card profile-new-loading-card">
          <Card.Body>
            <div className="profile-new-loading-wrap">
              <Spinner animation="border" size="sm" />
              <span>Loading account details...</span>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-4">
          <Col xl={4} lg={5}>
            <Card className="profile-new-card profile-new-summary-card">
              <Card.Body>
                <Image src={avatarUrl} roundedCircle width={112} className="profile-new-avatar" />
                <h2>{displayName}</h2>
                <p className="profile-new-email">{profile?.email || 'No email available'}</p>

                <div className="profile-new-detail-grid">
                  <div className="profile-new-detail-item">
                    <span>Username</span>
                    <strong>{profile?.username || '-'}</strong>
                  </div>
                  <div className="profile-new-detail-item">
                    <span>Member since</span>
                    <strong>{createdAtLabel}</strong>
                  </div>
                  <div className="profile-new-detail-item">
                    <span>Last login</span>
                    <strong>{lastLoginLabel}</strong>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="profile-new-card profile-new-stats-card">
              <Card.Body>
                <div className="profile-new-section-head">
                  <h3>Usage Snapshot</h3>
                  <Badge bg={profile?.is_active ? 'success' : 'secondary'}>
                    {profile?.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="profile-new-metrics">
                  <div className="profile-new-metric">
                    <span>Projects</span>
                    <strong>{stats.projects}</strong>
                  </div>
                  <div className="profile-new-metric">
                    <span>Documents Uploaded</span>
                    <strong>{stats.documents}</strong>
                  </div>
                  <div className="profile-new-metric">
                    <span>Test Cases Generated</span>
                    <strong>{stats.testCases}</strong>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={8} lg={7}>
            <Card className="profile-new-card profile-new-settings-card">
              <Card.Body>
                <Tabs
                  activeKey={activeKey}
                  onSelect={(k) => setActiveKey(k || 'profile')}
                  className="profile-new-tabs"
                >
                  <Tab
                    eventKey="profile"
                    title={(
                      <span className="profile-new-tab-title">
                        <PersonFill />
                        Profile
                      </span>
                    )}
                  >
                    <div className="profile-new-tab-panel">
                      <div className="profile-new-panel-head">
                        <div>
                          <h4>Personal Information</h4>
                          <p>Keep your contact details current for notifications and billing.</p>
                        </div>
                        {!isEditing ? (
                          <Button variant="outline-primary" size="sm" onClick={() => setIsEditing(true)}>
                            Edit Profile
                          </Button>
                        ) : (
                          <div className="profile-new-action-group">
                            <Button variant="success" size="sm" onClick={handleSave} disabled={isSavingProfile}>
                              {isSavingProfile ? 'Saving...' : 'Save'}
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={handleCancel} disabled={isSavingProfile}>
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>

                      <Form>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Full Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={profile?.full_name || ''}
                                disabled={!isEditing}
                                onChange={(e) => setProfile(profile ? { ...profile, full_name: e.target.value } : profile)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Email</Form.Label>
                              <Form.Control
                                type="email"
                                value={profile?.email || ''}
                                disabled={!isEditing}
                                onChange={(e) => setProfile(profile ? { ...profile, email: e.target.value } : profile)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-0">
                              <Form.Label>Username</Form.Label>
                              <Form.Control
                                type="text"
                                value={profile?.username || ''}
                                disabled={!isEditing}
                                onChange={(e) => setProfile(profile ? { ...profile, username: e.target.value } : profile)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </Tab>

                  <Tab
                    eventKey="security"
                    title={(
                      <span className="profile-new-tab-title">
                        <ShieldFill />
                        Security
                      </span>
                    )}
                  >
                    <div className="profile-new-tab-panel">
                      <div className="profile-new-panel-head profile-new-panel-head-stacked">
                        <div>
                          <h4>Security Settings</h4>
                          <p>Use a strong password and update credentials regularly.</p>
                        </div>
                      </div>

                      {forcePasswordSet && (
                        <Alert variant="warning" className="mb-3">
                          You signed in with a temporary password. Please set a new password to continue.
                        </Alert>
                      )}

                      <Form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const policyError = passwordPolicyError(newPassword, [profile?.email, profile?.username, profile?.full_name]);
                          if (policyError) {
                            setMessage(policyError);
                            setVariant('danger');
                            return;
                          }
                          if (newPassword !== confirmPassword) {
                            setMessage('Passwords do not match');
                            setVariant('danger');
                            return;
                          }
                            try {
                              setIsSavingPassword(true);
                            const payload = forcePasswordSet
                              ? {
                                  new_password: newPassword,
                                  confirm_password: confirmPassword,
                                }
                              : {
                                  current_password: currentPassword,
                                  new_password: newPassword,
                                  confirm_password: confirmPassword,
                                };
                            await apiClient.post(`${API_ENDPOINTS.CHANGE_PASSWORD}`, payload);
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                            sessionStorage.setItem(AUTH_NOTICE_MESSAGE, PASSWORD_CHANGED_NOTICE);
                            logout();
                            navigate('/login', {
                              replace: true,
                              state: { authNotice: PASSWORD_CHANGED_NOTICE },
                            });
                          } catch (err: any) {
                            setMessage(err?.response?.data?.detail || 'Failed to update password');
                            setVariant('danger');
                          } finally {
                            setIsSavingPassword(false);
                          }
                        }}
                      >
                        {!forcePasswordSet && (
                          <Form.Group className="mb-3">
                            <Form.Label>Current Password</Form.Label>
                            <Form.Control
                              type="password"
                              name="current_password"
                              placeholder="Enter current password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                            />
                          </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                          <Form.Label>New Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="new_password"
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Confirm New Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="confirm_password"
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                        </Form.Group>
                        <Button variant="primary" type="submit" disabled={isSavingPassword}>
                          {isSavingPassword
                            ? 'Saving...'
                            : (forcePasswordSet ? 'Set Password' : 'Update Password')}
                        </Button>
                      </Form>

                      <div className="profile-new-security-divider" />
                      <div className="profile-new-panel-head profile-new-panel-head-stacked mt-4">
                        <div>
                          <h4>Trusted Browsers</h4>
                          <p>Browsers remembered after MFA verification can skip email OTP until they expire.</p>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={trustedDevices.length === 0 || revokingDeviceId === 'all'}
                          onClick={handleRevokeAllTrustedDevices}
                        >
                          {revokingDeviceId === 'all' ? 'Removing...' : 'Remove All'}
                        </Button>
                      </div>

                      {isLoadingTrustedDevices ? (
                        <div className="profile-new-loading-wrap py-2">
                          <Spinner animation="border" size="sm" />
                          <span>Loading trusted browsers...</span>
                        </div>
                      ) : trustedDevices.length === 0 ? (
                        <p className="text-muted mb-0">No trusted browsers are currently saved.</p>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {trustedDevices.map((device) => (
                            <div key={device.id} className="d-flex align-items-center justify-content-between gap-3 border rounded px-3 py-2">
                              <div>
                                <strong>{device.device_name || 'Trusted browser'}</strong>
                                <div className="text-muted small">
                                  Last used {device.last_used_at ? formatLocalDateTimeFromUTC(device.last_used_at) : 'never'} - expires {device.expires_at ? formatLocalDateTimeFromUTC(device.expires_at) : 'unknown'}
                                </div>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={revokingDeviceId === device.id}
                                onClick={() => handleRevokeTrustedDevice(device.id)}
                              >
                                <Trash3 size={14} className="me-1" />
                                {revokingDeviceId === device.id ? 'Removing...' : 'Remove'}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Profile; 
